import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useForm, Controller } from 'react-hook-form';
import { Button, Text, Stack, TextField, Checkbox, Radio, Select, theme, Loader } from '@veeqo/ui';
import { AnimatePresence, motion } from 'framer-motion';

import { useAppContext } from 'context/Context';

import InputGroup from 'components/common/InputGroup';
import TermsConditions from 'components/Terms/TermsConditions';
import Divider from 'components/common/Divider';

import { trackSegmentEvent } from 'Marketing/utils/helpers';
import { Action, Event, Location } from 'Marketing/types';
import { accountCreationTracking } from 'Marketing/accountCreatedTracking';

import { submitSignupData, SignupResponse } from 'utils/api';
import states from 'utils/states.json';
import monthlyOrderVolumes from 'utils/monthlyOrderVolumes.json';
import formValidations from 'utils/validations';
import useDefaultParamsCountry from 'utils/useDefaultParamsCountry';
import { isMonthlyOrderVolumeVisible } from 'utils/isMonthlyOrderVolumeVisible';
import { getCompanyId } from 'utils/creditsReferral';

import { LetterBoxPlace, PlaceSuggestion } from 'hooks/usePlaceLookup/types';
import { usePlaceLookup } from 'hooks/usePlaceLookup/usePlaceLookup';

import { useTrackForm } from 'utils/adobeAnalytics/hooks/useTrackForm';
import { getECID } from 'utils/adobeAnalytics/utils/getECID';
import { SignupData } from 'context/types';
import { MARKETING_OPT_IN_COPY } from '../../constants';

type FormData = {
  companyName: string;
  addressLine1: string;
  country: string;
  city: string;
  region: string;
  postCode: string;
  phone: string;
  marketingConsent?: boolean;
  monthlyOrderVolume: string;
  referralCode: string;
};

const Form = styled.form`
  .form__submit-button {
    width: 100%;
  }

  .form__login-button,
  .form__email-button {
    flex: 1 0 auto;
  }

  .stack--full-width {
    width: 100%;
  }
`;

export const Hint = styled.span`
  display: block;
  font-family: ${theme.fontFamily};
  font-size: ${theme.sizes[3]};
  line-height: ${theme.sizes[4]};
  color: ${theme.colors.neutral.ink.light};
`;

export const countryOptions = [
  { value: 'US', label: 'United States' },
  { value: 'GB', label: 'United Kingdom' },
  { value: 'other', label: 'Other' },
];

const CompanyDetailsForm = () => {
  const navigate = useNavigate();

  const {
    actions,
    state: { signupData },
  } = useAppContext();
  const country = useDefaultParamsCountry();
  const isSelectedCountryUS = country === 'US';
  const postCodeValidationMessage = 'Please enter a valid postcode/zip code';

  const defaultValues = signupData;
  const {
    control,
    handleSubmit,
    getValues,
    setValue,
    reset,
    formState: { errors },
  } = useForm<FormData>({ defaultValues });

  const [showManual, setShowManual] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [addressInput, setAddressInput] = useState('');
  const [isButtonLoading, setIsButtonLoading] = useState(false);
  const [signupErrorMessage, setSignupErrorMessage] = useState('');

  const shouldShowMonthlyOrderSelect = useMemo(() => isMonthlyOrderVolumeVisible(), []);
  const showManualOrAnyError = showManual || Object.keys(errors).length;

  const { trackFormUpdated } = useTrackForm('account-setup-form');

  const {
    suggestions,
    loading,
    error,
    onChange: onSearchChange,
    onSelect,
    resetStates,
  } = usePlaceLookup();

  const resetAddressFields = (value: string) => {
    actions.setSignupData({ country: value });
    resetStates();
    setShowManual(false);
    setShowSuggestions(false);
    setAddressInput('');

    reset({
      country: value,
      companyName: getValues('companyName'),
      monthlyOrderVolume: getValues('monthlyOrderVolume'),
    });
  };

  const onCountryChange = (value: string) => {
    if (value === 'other') {
      navigate('/signup/coming-soon');
    }
    resetAddressFields(value);
  };

  const handleAddressChange = (value: string) => {
    setAddressInput(value);
    onSearchChange(value, country);
  };

  const handleSuggestionSelect = (suggestion: PlaceSuggestion) => {
    onSelect({
      suggestion,
      onSuccess: (place: LetterBoxPlace) => {
        setValue('addressLine1', place.addressLine1);
        setValue('city', place.city);
        setValue('region', place.stateOrRegion);
        setValue('postCode', place.postalCode);
        setShowManual(true);
      },
    });
    setShowSuggestions(false);
  };

  const handleManualAddressClick = () => {
    setShowManual(true);
    setShowSuggestions(false);
    trackSegmentEvent(Event.BUTTON_CLICKED, {
      location: Location.SIGNUP_COMPANY_DETAILS,
      action: Action.ENTER_ADDRESS_MANUALLY,
    });
  };

  const [signupBlocked, setSignupBlocked] = useState(false);
  const handleSignupError = (error_messages: string[]) => {
    if (error_messages.includes('Email has already been taken')) {
      setSignupErrorMessage(
        'This email is currently in use. Please login to continue or try another email',
      );
      setSignupBlocked(true);
      return;
    }
    if (error_messages.includes('Email is invalid')) {
      setSignupErrorMessage('Please enter a valid email address');
      setSignupBlocked(true);
      return;
    }
    if (error_messages.includes('Post code format is not valid, please check')) {
      return setSignupErrorMessage(postCodeValidationMessage);
    }
    // handle unspecified use case
    if (error_messages.length >= 1) {
      return setSignupErrorMessage(error_messages[0]);
    }
    // default
    setSignupErrorMessage('There has been an error, please try again');
  };

  const onSubmit = handleSubmit(async (formData: FormData) => {
    setIsButtonLoading(true);

    const submitPayload: SignupData = {
      ...signupData,
      ...formData,
      referralCompanyID: getCompanyId(formData.referralCode),
      phone: formData.phone?.trim(),
      country,
      termsAndConditions: true,
      adobeECID: getECID(),
    };

    accountCreationTracking(submitPayload);
    const pathPrefix = global.location.pathname.startsWith('/vq') ? '/vq' : '';
    const response: SignupResponse | undefined = await submitSignupData(submitPayload, pathPrefix);

    if (response?.error_messages) {
      setIsButtonLoading(false);
      handleSignupError(response.error_messages);
    } else {
      localStorage.removeItem('MonthlyOrderVolumeSelectEnabled');
    }
  });

  useEffect(() => {
    if (global.location.pathname.startsWith('/vq')) return;
    if (!signupData.email) {
      if (global.location.host === 'app.veeqo.com') {
        navigate('/signup');
      } else {
        console.log("should navigate('/signup');");
      }
    }
  });

  useEffect(() => {
    return () => {
      resetStates();
    };
  }, [resetStates]);

  return (
    <Form onSubmit={onSubmit} id="account-setup-form">
      <Stack spacing={6}>
        <InputGroup>
          <Controller
            name="companyName"
            control={control}
            rules={formValidations.companyName}
            render={({ field: { onChange, value } }) => (
              <TextField
                onChange={onChange}
                onBlur={trackFormUpdated('companyName')}
                value={value}
                placeholder="Business name"
                aria-label="company name"
                label="Company Name"
                hint="This name will appear on invoices and shipping labels"
                error={errors.companyName && errors.companyName.message}
                className="fs-exclude"
              />
            )}
          />
        </InputGroup>
        {
          /* Seller info group */
          shouldShowMonthlyOrderSelect && (
            <InputGroup>
              <Controller
                name="monthlyOrderVolume"
                control={control}
                rules={formValidations.monthlyOrderVolume}
                render={({ field: { onChange, value } }) => (
                  <Select
                    label="Orders per month"
                    value={value}
                    options={monthlyOrderVolumes}
                    onChange={onChange}
                    onBlur={trackFormUpdated('monthlyOrderVolume')}
                    error={errors.monthlyOrderVolume && errors.monthlyOrderVolume.message}
                    className="fs-exclude"
                  />
                )}
              />
            </InputGroup>
          )
        }
        {/* Address Group */}
        <InputGroup style={{ marginTop: '1rem' }}>
          <Controller
            name="country"
            control={control}
            rules={formValidations.country}
            render={({ field: { value } }) => (
              <Select
                label="Country"
                value={value}
                options={countryOptions}
                onChange={(e) => {
                  onCountryChange(e);
                }}
                onBlur={trackFormUpdated('country')}
              />
            )}
          />
        </InputGroup>
        <InputGroup>
          <TextField
            value={addressInput}
            onFocus={() => setShowSuggestions(true)}
            onChange={handleAddressChange}
            onBlur={trackFormUpdated('addressSuggestionsInput')}
            aria-label="company address"
            label="Company Address"
            placeholder="Search your address"
            hint="Your company location is used to calculate shipping rates"
            className="fs-exclude"
            error={error || undefined}
          />
        </InputGroup>

        {loading && (
          <Stack direction="horizontal" alignX="center" style={{ width: '100%' }}>
            <Loader type="TailSpin" />
          </Stack>
        )}

        {suggestions.length > 0 && (showSuggestions || !showManual) && (
          <Stack spacing={2}>
            {suggestions.map((suggestion: PlaceSuggestion) => (
              <span key={suggestion.id} className="fs-exclude">
                <Radio
                  label={suggestion.text}
                  checked={false}
                  onChange={() => {
                    handleSuggestionSelect(suggestion);
                    trackFormUpdated('addressSuggestion')();
                  }}
                />
              </span>
            ))}
          </Stack>
        )}

        {!showManualOrAnyError && (
          <Button variant="link" onClick={handleManualAddressClick}>
            Enter address manually
          </Button>
        )}
        <InputGroup style={showManualOrAnyError ? {} : { display: 'none' }}>
          <Controller
            name="addressLine1"
            control={control}
            rules={formValidations.addressLine1}
            render={({ field: { onChange, value } }) => (
              <TextField
                onChange={onChange}
                onBlur={trackFormUpdated('addressLine1')}
                value={value}
                placeholder="Address"
                aria-label="address"
                label="Address"
                error={errors.addressLine1 && errors.addressLine1.message}
                className="fs-exclude"
              />
            )}
          />
        </InputGroup>
        {isSelectedCountryUS ? (
          <InputGroup style={showManualOrAnyError ? {} : { display: 'none' }}>
            <Controller
              name="city"
              control={control}
              rules={formValidations.city}
              render={({ field: { onChange, value } }) => (
                <TextField
                  onChange={onChange}
                  onBlur={trackFormUpdated('city')}
                  value={value}
                  label="City"
                  placeholder="City"
                  aria-label="city"
                  error={errors.city && errors.city.message}
                  className="fs-exclude"
                />
              )}
            />
          </InputGroup>
        ) : null}
        <Stack
          direction="horizontal"
          className="stack--full-width"
          style={showManualOrAnyError ? {} : { display: 'none' }}
        >
          {isSelectedCountryUS ? (
            <InputGroup>
              <Controller
                name="region"
                control={control}
                rules={formValidations.region}
                render={({ field: { onChange, value } }) => (
                  <Select
                    label="State"
                    value={value}
                    options={states}
                    onChange={onChange}
                    onBlur={trackFormUpdated('region')}
                    error={errors.region && errors.region.message}
                    className="fs-exclude"
                  />
                )}
              />
            </InputGroup>
          ) : (
            <InputGroup>
              <Controller
                name="city"
                control={control}
                rules={formValidations.city}
                render={({ field: { onChange, value } }) => (
                  <TextField
                    onChange={onChange}
                    onBlur={trackFormUpdated('city')}
                    value={value}
                    label="City"
                    placeholder="City"
                    aria-label="city"
                    error={errors.city && errors.city.message}
                    className="fs-exclude"
                  />
                )}
              />
            </InputGroup>
          )}
          <InputGroup>
            <Controller
              name="postCode"
              control={control}
              rules={formValidations.postCode}
              render={({ field: { onChange, value } }) => (
                <TextField
                  onChange={onChange}
                  onBlur={trackFormUpdated('postCode')}
                  value={value}
                  placeholder={isSelectedCountryUS ? '12345' : 'SA1 1NW'}
                  aria-label={isSelectedCountryUS ? 'zipcode' : 'postcode'}
                  label={isSelectedCountryUS ? 'Zipcode' : 'Postcode'}
                  error={errors.postCode && errors.postCode.message}
                  className="fs-exclude"
                />
              )}
            />
          </InputGroup>
        </Stack>
        {/* Address Group */}
        <Stack spacing={6} className="stack--full-width">
          <InputGroup>
            <Controller
              name="phone"
              control={control}
              render={({ field: { onChange, value } }) => (
                <TextField
                  onChange={onChange}
                  onBlur={trackFormUpdated('phone')}
                  value={value}
                  aria-label="phone number"
                  label="Phone Number"
                  placeholder={isSelectedCountryUS ? '+1 123 456 7890' : '+44 0203 808 9165'}
                  error={errors.phone && errors.phone.message}
                  className="fs-exclude"
                />
              )}
            />
          </InputGroup>
          {!isSelectedCountryUS && (
            <AnimatePresence>
              <motion.div initial={{ height: 0 }} animate={{ height: 'auto' }}>
                <Controller
                  name="marketingConsent"
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <Checkbox label={MARKETING_OPT_IN_COPY} onChange={onChange} checked={!!value} />
                  )}
                />
              </motion.div>
            </AnimatePresence>
          )}
        </Stack>
        <Divider />
        <InputGroup>
          <Controller
            name="referralCode"
            control={control}
            render={({ field: { onChange, value } }) => (
              <TextField
                onChange={onChange}
                onBlur={trackFormUpdated('referralCode')}
                value={value}
                aria-label="referral code"
                type="text"
                label="Referral code"
                placeholder="Referral code"
                error={errors.referralCode && errors.referralCode.message}
              />
            )}
          />
        </InputGroup>
        {signupErrorMessage && signupBlocked && (
          <Stack direction="vertical" spacing={6} style={{ width: '100%' }} alignX="center">
            <Text variant="errorSmall">{signupErrorMessage}</Text>
            <Stack direction="horizontal" spacing={6} style={{ width: '100%' }} alignX="center">
              <Button
                variant="default"
                onClick={() => navigate('/signin')}
                className="form__login-button"
              >
                Login
              </Button>
              <Button
                variant="primary"
                onClick={() => navigate('/signup')}
                className="form__email-button"
              >
                Enter a new email
              </Button>
            </Stack>
          </Stack>
        )}
        {signupErrorMessage && !signupBlocked && (
          <Stack direction="vertical" spacing={6} style={{ width: '100%' }} alignX="center">
            <Text variant="errorSmall">{signupErrorMessage}</Text>
            <Button
              type="submit"
              variant="primary"
              loading={isButtonLoading}
              disabled={isButtonLoading}
              className="form__submit-button"
            >
              Connect store
            </Button>
          </Stack>
        )}
        {!signupErrorMessage && (
          <Button
            type="submit"
            variant="primary"
            loading={isButtonLoading}
            disabled={isButtonLoading}
            className="form__submit-button"
          >
            Connect store
          </Button>
        )}
        <TermsConditions country={country} />
      </Stack>
    </Form>
  );
};

export default CompanyDetailsForm;
